import React, { useState } from "react";
import styles from "./BetTableBtTable.module.css";

// Info tooltip component
const InfoTooltip = ({ min, max, tooltipId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const formatLimit = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value?.toFixed(1) || "100.0";
  };

  return (
    <div className={styles.infoBlock} onClick={(e) => e.stopPropagation()}>
      <a
        href=""
        data-toggle="collapse"
        data-target={`#${tooltipId}`}
        aria-expanded={isOpen}
        className={`${styles.infoIcon} ${isOpen ? '' : 'collapsed'}`}
        onClick={toggleTooltip}
      >
        <i className="fas fa-info-circle m-l-10"></i>
      </a>
      <div
        id={tooltipId}
        className={`${styles.minMaxInfo} collapse ${isOpen ? 'show' : ''}`}
      >
        <span className={styles.mr5}>
          <b>Min:</b> <span>{formatLimit(min || 100)}</span>
        </span>
        <span className={styles.mr5}>
          <b>Max:</b> <span>{formatLimit(max || 100000)}</span>
        </span>
      </div>
    </div>
  );
};

export default function BetTableBtTable({ runnersData = [], exposures = {} }) {
  // Helper function to get bet data by sid
  const getBetData = (sid) => {
    return runnersData.find(item => item?.sid === sid) || {};
  };

  // Helper function to get exposure
  const getExposure = (runnerName) => {
    const exposureKeys = [
      runnerName,
      runnerName?.toLowerCase(),
      runnerName?.trim()
    ];
    
    for (const key of exposureKeys) {
      if (key && exposures[key] !== undefined) {
        return exposures[key];
      }
    }
    return 0;
  };

  // Format exposure value
  const formatExposure = (value) => {
    if (value === null || value === undefined || value === 0) return "0";
    return value < 0 ? value.toFixed(0) : `+${value.toFixed(0)}`;
  };

  // Check if bet is suspended
  const isSuspended = (betData) => {
    return betData?.gstatus?.toUpperCase() === "SUSPENDED" || !betData || betData.b === 0 || betData.b === null || betData.b === undefined;
  };

  // Main betting options A-F (sid 1-6)
  const mainOptions = [
    { sid: 1, label: "A.", name: "Don" },
    { sid: 2, label: "B.", name: "Amar Akbar Anthony" },
    { sid: 3, label: "C.", name: "Sahib Bibi Aur Ghulam" },
    { sid: 4, label: "D.", name: "Dharam Veer" },
    { sid: 5, label: "E.", name: "Kis Kis Ko Pyaar Karoon" },
    { sid: 6, label: "F.", name: "Ghulam" },
  ].map(option => ({
    ...option,
    betData: getBetData(option.sid)
  }));

  // Odd option (sid 7)
  const oddOption = getBetData(7);

  // Dulha Dulhan and Barati (sid 14-15)
  const dulhaDulhan = getBetData(14);
  const barati = getBetData(15);

  // Red/Black options (sid 8-9)
  const redOption = getBetData(8);
  const blackOption = getBetData(9);

  // Card options (sid 10-13) - but display cards 11, 12, 13, 1 from andar_bahar
  const cardOptions = [
    { sid: 10, cardNum: 11, name: "Card J" },
    { sid: 11, cardNum: 12, name: "Card Q" },
    { sid: 12, cardNum: 13, name: "Card K" },
    { sid: 13, cardNum: 1, name: "Card A" },
  ].map(card => ({
    ...card,
    betData: getBetData(card.sid)
  }));

  // Get card image URL
  const getCardImageUrl = (cardNum) => {
    return `/assets/img/card/${cardNum}.jpg`;
  };

  // Get first bet data for min/max
  const firstBetData = mainOptions[0]?.betData || {};

  return (
    <div className={styles.container}>
      {/* Section 1: Main betting options A-F */}
      <div className={`${styles.aaaContent} m-t-10`}>
        <div className="row">
          <div className="col-12 text-right">
            <div className={styles.infoBlock}>
              <InfoTooltip
                min={firstBetData?.min || 100}
                max={firstBetData?.max || 100000}
                tooltipId="min-max-info1"
              />
            </div>
          </div>
        </div>
        <div className="row">
          {mainOptions.map((option) => {
            const exposure = getExposure(option.betData?.nat || option.name);
            const backValue = option.betData?.b || 0;
            const layValue = option.betData?.l || 0;
            const backSuspended = isSuspended(option.betData) || backValue === 0;
            const laySuspended = isSuspended(option.betData) || layValue === 0;

            return (
              <div key={option.sid} className="col-4 text-center">
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex justify-content-center align-items-center">
                      
                        <div className="text-danger">{option.label}</div>
                        <div style={{color:'white'}}>         {option.name}</div>
                        </div>
                    
                
                  </div>
                  <div className={`${styles.aaaButton} col-12`}>
                    <button 
                      className={`back ${backSuspended ? styles.suspended : ''}`}
                      style={{ cursor: 'default', position: 'relative' }}
                    >
                      <span className="odd">{backSuspended ? "0" : backValue}</span>
                    </button>
                    <button 
                      className={`lay ${laySuspended ? styles.suspended : ''}`}
                      style={{ cursor: 'default', position: 'relative' }}
                    >
                      <span className="odd">{laySuspended ? "0" : layValue}</span>
                    </button>
                  </div>
                  <div className="col-12">
                    <span className="d-block m-t-5" style={{ color: 'black' }}>
                      {formatExposure(exposure)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: Odd and Dulha/Barati */}
      <div className="row m-t-10">
        {/* Odd Section */}
        <div className="col-4 text-center">
          <div className="row">
            <div className="col-12">
              <div className={styles.aaaContent}>
                <div className="text-right">
                  <div className={styles.infoBlock}>
                    <InfoTooltip
                      min={oddOption?.min || 100}
                      max={oddOption?.max || 100000}
                      tooltipId="min-max-info2"
                    />
                  </div>
                </div>
                <div className="text-center row">
                  <div className="col-12">
                    <span className="d-block">
                      <span className="d-block">
                        <div className="d-flex justify-content-center align-items-center">
                          <div className="text-white">Odd</div>
                          <div style={{color:'white'}}>         </div>
                        </div>
                      </span>
                    </span>
                  </div>
                  <div className={`${styles.aaaButton} col-12`}>
                    <button 
                      className={`back ${isSuspended(oddOption) || !oddOption?.b ? styles.suspended : ''}`}
                      style={{ cursor: 'default', position: 'relative' }}
                    >
                      <span className="odd">{isSuspended(oddOption) || !oddOption?.b ? "0" : oddOption.b}</span>
                    </button>
                    <button 
                      className={`lay ${isSuspended(oddOption) || !oddOption?.l ? styles.suspended : ''}`}
                      style={{ cursor: 'default', position: 'relative' }}
                    >
                      <span className="odd">{isSuspended(oddOption) || !oddOption?.l ? "0" : oddOption.l}</span>
                    </button>
                  </div>
                  <div className="col-12">
                    <span className="d-block text-center m-t-5" style={{ color: 'black' }}>
                      {formatExposure(getExposure(oddOption?.nat || "Odd"))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dulha Dulhan and Barati */}
        <div className="col-8 p-l-5">
          <div className={styles.aaaContent}>
            <div className="row">
              <div className="col-6">
                <div className="text-right">
                  <div className={styles.infoBlock}>
                    <InfoTooltip
                      min={dulhaDulhan?.min || 100}
                      max={dulhaDulhan?.max || 100000}
                      tooltipId="min-max-info3"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <div>
                    <div className="d-flex justify-content-center align-items-center">
                      <div className="text-white">{dulhaDulhan?.b || "0"}</div>
                    </div>
                  </div>
                  <div className="m-b-5 m-t-10">
                    <button className={`text-uppercase ${styles.lowHighBtn} ${isSuspended(dulhaDulhan) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
                      Dulha Dulhan K-Q
                    </button>
                  </div>
                  <div style={{ color: 'black' }}>
                    {formatExposure(getExposure(dulhaDulhan?.nat || "Dulha Dulhan K-Q"))}
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="text-right">
                  <div className={styles.infoBlock}>
                    <InfoTooltip
                      min={barati?.min || 100}
                      max={barati?.max || 100000}
                      tooltipId="min-max-info4"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div className="text-white">{barati?.b || "0"}</div>
                  </div>
                  <div className="m-b-5 m-t-10">
                    <button className={`text-uppercase ${styles.lowHighBtn} ${isSuspended(barati) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
                      Barati J-A
                    </button>
                  </div>
                  <div style={{ color: 'black' }}>
                    {formatExposure(getExposure(barati?.nat || "Barati J-A"))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Red/Black and Cards */}
      <div className="row m-t-10">
        {/* Red/Black Section */}
        <div className="col-6 p-r-5">
          <div className={styles.aaaContent}>
            <div className="row">
              <div className="col-12">
                <div className="text-right">
                  <div className={styles.infoBlock}>
                    <InfoTooltip
                      min={redOption?.min || 100}
                      max={redOption?.max || 100000}
                      tooltipId="min-max-info5"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <div className="text-center">
                  <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div className="text-white">{redOption?.b || "0"}</div>
                  </div>
                  <div className="m-b-5 m-t-5">
                    <button className={`text-uppercase ${styles.lowHighBtn} ${isSuspended(redOption) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
                      <span className={styles.cardIcon}>
                        <span className={styles.cardRed}>[</span>
                      </span>
                      <span className={styles.cardIcon}>
                        <span className={styles.cardRed}>{'{'}</span>
                      </span>
                    </button>
                  </div>
                  <div style={{ color: 'black' }}>
                    {formatExposure(getExposure(redOption?.nat || "Red"))}
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="text-center">
                  <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div className="text-white">{blackOption?.b || "0"}</div>
                  </div>
                  <div className="m-b-5 m-t-5">
                    <button className={`text-uppercase ${styles.lowHighBtn} ${isSuspended(blackOption) ? styles.suspended : ''}`} style={{ position: 'relative' }}>
                      <span className={styles.cardIcon}>
                        <span className={styles.cardBlack}>]</span>
                      </span>
                      <span className={styles.cardIcon}>
                        <span className={styles.cardBlack}>{'}'}</span>
                      </span>
                    </button>
                  </div>
                  <div style={{ color: 'black' }}>
                    {formatExposure(getExposure(blackOption?.nat || "Black"))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cards Section */}
        <div className="col-6 p-l-5">
          <div className={`aaa-content ${styles.aaaContent}`}>
            <div className="row">
              <div className="col-8 text-right">
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <div className="text-white text-center">Cards {cardOptions[0]?.betData?.b || "0"}</div>
                </div>
              </div>
              <div className="col-4">
                <div className="text-right">
                  <div className={styles.infoBlock}>
                    <InfoTooltip
                      min={cardOptions[0]?.betData?.min || 100}
                      max={cardOptions[0]?.betData?.max || 25000}
                      tooltipId="min-max-info6"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center m-t-10">
              <div className="m-b-5 m-t-5" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5px' }}>
                {cardOptions.map((card) => {
                  const exposure = getExposure(card.betData?.nat || card.name);
                  const cardSuspended = isSuspended(card.betData);

                  return (
                    <div key={card.sid} className={`m-r-5 card-image ${styles.cardImage}`}>
                      <div className={`casino-box ${cardSuspended ? 'suspended' : ''}`}>
                        <img 
                          src={getCardImageUrl(card.cardNum)} 
                          alt={card.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className={`ubook text-center m-t-5`} style={{ color: 'black' }}>
                        {formatExposure(exposure)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

